/**
 * Dialog Variables File Renderer
 * Generates DialogVariable class and static DialogVariables class with all dialog variables
 */

/**
 * Render dialog variables file for Unity C# exports
 * Generates DialogVariable class and static DialogVariables class with all dialog variables
 * 
 * @param dialogs - Array of dialog objects with variable definitions
 * @param namespace - C# namespace (default: 'Grimoire.Data')
 * @param className - Class name for the static variables class (default: 'DialogVariables')
 */
export function renderDialogVariablesFile(
  dialogs: any[],
  namespace: string = 'Grimoire.Data',
  className: string = 'DialogVariables'
): string {
  const hasDialogs = dialogs && dialogs.length > 0;

  // Collect all unique variables from all dialogs
  const variablesMap = new Map<string, { name: string; type: 'boolean' | 'number'; initialValue: boolean | number; dialogName: string; description?: string }>();
  
  if (hasDialogs) {
    console.log(`📊 Rendering dialog variables file from ${dialogs.length} dialogs`);
    
    dialogs.forEach(dialog => {
      const dialogName = dialog.name || dialog.dialog_key || 'unknown';
      const variables = dialog.data?.variables;
      
      if (variables && Array.isArray(variables)) {
        variables.forEach((variable: any) => {
          const varName = variable.name;
          if (varName) {
            // Create a unique key: dialogKey_variableName
            const dialogKey = dialog.dialog_key || dialog.name;
            const uniqueKey = `${dialogKey}_${varName}`;
            
            if (!variablesMap.has(uniqueKey)) {
              variablesMap.set(uniqueKey, {
                name: varName,
                type: variable.type || 'boolean',
                initialValue: variable.initialValue ?? (variable.type === 'number' ? 0 : false),
                dialogName: dialogName,
                description: variable.description
              });
            }
          }
        });
      }
    });
  }

  if (variablesMap.size === 0) {
    console.log('⚠️ No variables found in dialogs - generating empty file');
  } else {
    console.log(`📊 Found ${variablesMap.size} unique dialog variables`);
  }

  let output = '// Auto-generated Dialog Variables\n';
  output += '// This file is AUTO-GENERATED and can be safely regenerated.\n';
  output += '// DO NOT EDIT THIS FILE DIRECTLY - your changes will be lost.\n\n';
  output += `namespace ${namespace}\n{\n`;

  // Generate DialogVariable class
  output += '\t/// <summary>Represents a dialog variable that can be boolean or number</summary>\n';
  output += '\tpublic class DialogVariable\n\t{\n';
  output += '\t\tpublic string Name { get; }\n';
  output += '\t\tpublic string Type { get; }\n';
  output += '\t\tprivate object _value;\n\n';
  
  output += '\t\tpublic DialogVariable(string name, string type, object initialValue)\n\t\t{\n';
  output += '\t\t\tName = name;\n';
  output += '\t\t\tType = type;\n';
  output += '\t\t\t_value = initialValue;\n';
  output += '\t\t}\n\n';
  
  // Boolean value property
  output += '\t\t/// <summary>Get or set the value as a boolean (for boolean variables)</summary>\n';
  output += '\t\tpublic bool BoolValue\n\t\t{\n';
  output += '\t\t\tget => Type == "boolean" && _value is bool b ? b : false;\n';
  output += '\t\t\tset { if (Type == "boolean") _value = value; }\n';
  output += '\t\t}\n\n';
  
  // Number value property
  output += '\t\t/// <summary>Get or set the value as a number (for number variables)</summary>\n';
  output += '\t\tpublic float NumberValue\n\t\t{\n';
  output += '\t\t\tget => Type == "number" && _value is float f ? f : Type == "number" && _value is int i ? i : 0f;\n';
  output += '\t\t\tset { if (Type == "number") _value = value; }\n';
  output += '\t\t}\n\n';
  
  // Generic get method
  output += '\t\t/// <summary>Get the current value</summary>\n';
  output += '\t\tpublic object GetValue() => _value;\n\n';
  
  // Implicit conversions
  output += '\t\t/// <summary>Implicit conversion to bool (for boolean variables)</summary>\n';
  output += '\t\tpublic static implicit operator bool(DialogVariable variable) => variable?.BoolValue ?? false;\n\n';
  
  output += '\t\t/// <summary>Implicit conversion to float (for number variables)</summary>\n';
  output += '\t\tpublic static implicit operator float(DialogVariable variable) => variable?.NumberValue ?? 0f;\n\n';
  
  output += '\t\t/// <summary>Reset to initial value</summary>\n';
  output += '\t\tpublic void Reset(object initialValue) => _value = initialValue;\n';
  
  output += '\t}\n\n';

  // Generate DialogVariables static class
  output += `\t/// <summary>Static class containing all dialog variables</summary>\n`;
  output += `\tpublic static class ${className}\n\t{\n`;

  // Group variables by dialog
  const variablesByDialog = new Map<string, Array<{ uniqueKey: string; data: any }>>();
  
  variablesMap.forEach((data, uniqueKey) => {
    const dialogKey = uniqueKey.split('_')[0];
    if (!variablesByDialog.has(dialogKey)) {
      variablesByDialog.set(dialogKey, []);
    }
    variablesByDialog.get(dialogKey)!.push({ uniqueKey, data });
  });

  // Output variables grouped by dialog
  if (variablesByDialog.size === 0) {
    output += '\t\t// No dialog variables defined\n';
  } else {
    variablesByDialog.forEach((variables, dialogKey) => {
      const dialogClassName = dialogKey.replace(/[^a-zA-Z0-9_]/g, '_');
      output += `\t\t/// <summary>Variables for dialog: ${variables[0].data.dialogName}</summary>\n`;
      output += `\t\tpublic static class ${dialogClassName}\n\t\t{\n`;
      
      variables.forEach(({ uniqueKey, data }) => {
        const safeName = data.name.replace(/[^a-zA-Z0-9_]/g, '_');
        const csharpType = data.type === 'number' ? 'float' : 'bool';
        const initialValueStr = data.type === 'number' ? `${data.initialValue}f` : (data.initialValue ? 'true' : 'false');
        
        if (data.description) {
          output += `\t\t\t/// <summary>${data.description.replace(/"/g, '\\"')}</summary>\n`;
        }
        output += `\t\t\tpublic static readonly DialogVariable ${safeName} = new("${data.name}", "${data.type}", ${initialValueStr});\n`;
      });
      
      output += '\t\t}\n\n';
    });
  }

  output += '\t}\n';
  output += '}\n';

  if (variablesMap.size > 0) {
    console.log('✅ Dialog variables file rendered successfully');
  } else {
    console.log('✅ Empty dialog variables file rendered successfully');
  }
  return output;
}
